import { AccessorialItem, TMSEquipmentType } from 'types/Load';

import { MGB_PRIORITY_ACCESSORIAL_CODES } from './constants';

const ACCESSORIAL_VALUE_SEP = ' - ';

const DEFAULT_HAZMAT_LABEL = 'Hazardous Material';

export function toAccessorialOptionValue(code: string, name: string): string {
  return `${code}${ACCESSORIAL_VALUE_SEP}${name}`;
}

export function fromAccessorialOptionValue(value: string): {
  code: string;
  name: string;
} {
  const sepIndex = value.indexOf(ACCESSORIAL_VALUE_SEP);
  if (sepIndex === -1) {
    return { code: value, name: value };
  }
  return {
    code: value.slice(0, sepIndex),
    name: value.slice(sepIndex + ACCESSORIAL_VALUE_SEP.length),
  };
}

function buildAccessorialOptions(list: TMSEquipmentType[]) {
  const seen = new Set<string>();
  const options: { label: string; value: string }[] = [];

  for (const item of list) {
    const code = item.id ?? '';
    const name = item.name ?? '';
    if (!code && !name) {
      continue;
    }

    const value = toAccessorialOptionValue(code, name);
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);

    const label =
      code && name.toLowerCase() !== code.toLowerCase()
        ? `${name} (${code})`
        : (name ?? code);

    options.push({ label, value });
  }

  return options;
}

function sortAccessorialOptions(
  options: { label: string; value: string }[]
): { label: string; value: string }[] {
  const priority = new Map(
    MGB_PRIORITY_ACCESSORIAL_CODES.map((code, index) => [
      code.toUpperCase(),
      index,
    ])
  );

  return [...options].sort((left, right) => {
    const leftCode = fromAccessorialOptionValue(left.value).code.toUpperCase();
    const rightCode = fromAccessorialOptionValue(
      right.value
    ).code.toUpperCase();
    const leftRank = priority.get(leftCode) ?? Number.MAX_SAFE_INTEGER;
    const rightRank = priority.get(rightCode) ?? Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return left.label.localeCompare(right.label);
  });
}

export function accessorialCode(item: AccessorialItem): string {
  if (typeof item.value === 'string' && item.value.trim()) {
    return item.value.trim();
  }
  if (typeof item.value === 'number' && !Number.isNaN(item.value)) {
    return String(item.value);
  }
  return String(item.id ?? '');
}

export function accessorialToOptionValue(item: AccessorialItem): string {
  return toAccessorialOptionValue(accessorialCode(item), item.displayText);
}

export function optionValuesToAccessorials(
  values: string[]
): AccessorialItem[] {
  return values.map((value) => {
    const { code, name } = fromAccessorialOptionValue(value);
    return {
      id: Number.parseInt(code, 10) || 0,
      displayText: name,
      value: code,
    };
  });
}

export function accessorialsFromRateLineItems(
  lineItems:
    | Array<{
        label?: string | null;
        accessorialCode?: string | null;
      }>
    | null
    | undefined,
  hazmatLabel: string = DEFAULT_HAZMAT_LABEL
): AccessorialItem[] {
  const seen = new Set<string>();
  const out: AccessorialItem[] = [];

  for (const item of lineItems ?? []) {
    if ((item.label ?? '').trim() === hazmatLabel) {
      continue;
    }
    const code = (item.accessorialCode ?? '').trim();
    if (!code || seen.has(code)) {
      continue;
    }
    seen.add(code);
    const name = (item.label ?? '').trim() || code;
    out.push({
      id: Number.parseInt(code, 10) || 0,
      displayText: name,
      value: code,
    });
  }

  return out;
}

export function shouldMirrorAccessorialsFromRates(
  nextFromRates: AccessorialItem[],
  ratesAccessorialsActivated: boolean
): boolean {
  return nextFromRates.length > 0 || ratesAccessorialsActivated;
}

/** Options for Specs multi-select and Rates charge dropdowns. */
export function buildMGBAccessorialSelectOptions(
  serviceTypes: TMSEquipmentType[] | null | undefined,
  extraItems: AccessorialItem[] = []
): { label: string; value: string }[] {
  const base = buildAccessorialOptions(serviceTypes ?? []);
  const seen = new Set(base.map((option) => option.value));

  for (const item of extraItems) {
    const code = accessorialCode(item);
    const name = item.displayText?.trim() ?? '';
    if (!code && !name) {
      continue;
    }
    const value = toAccessorialOptionValue(code, name);
    if (seen.has(value)) {
      continue;
    }
    seen.add(value);
    const label =
      code && name.toLowerCase() !== code.toLowerCase()
        ? `${name} (${code})`
        : (name ?? code);
    base.push({ label, value });
  }

  return sortAccessorialOptions(base);
}
